import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Calendar as CalendarIcon, Clock, Flag, FolderKanban, 
  Loader2, User, Briefcase, Users, Repeat, Hash, ShieldCheck, Globe, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-green-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-red-500' }
];

const RECURRENCE_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
];

const UnifiedTaskModal = ({ 
  open, 
  onOpenChange, 
  onTaskCreated,
  defaultWorkspaceId = null,
  defaultProjectId = null,
  editTask = null
}) => {
  const { workspaces, currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  
  // Find personal workspace
  const personalWorkspace = workspaces.find(w => 
    w.workspace_type === 'personal' || w.name === 'Personal'
  );
  const personalWsId = personalWorkspace?.workspace_id || '';

  // Determine if default workspace is personal
  const getInitialPersonalState = () => {
    if (defaultWorkspaceId) {
      const ws = workspaces.find(w => w.workspace_id === defaultWorkspaceId);
      return ws?.workspace_type === 'personal';
    }
    return true; // Default to personal workspace
  };

  const isPersonalWs = getInitialPersonalState();
  const today = new Date();
  
  // Default workspace: always use personal workspace unless a specific one is provided
  const defaultWsId = defaultWorkspaceId || personalWsId || currentWorkspace?.workspace_id || '';
  
  const [task, setTask] = useState({
    title: '',
    description: '',
    workspace_id: defaultWsId,
    project_id: defaultProjectId || '',
    due_date: isPersonalWs ? today : null,
    due_time: '',
    priority: 'medium',
    assigned_to: isPersonalWs ? (user?.user_id || '') : '',
    is_recurring: false,
    recurrence_frequency: 'weekly',
    recurrence_end_date: null,
    value: '',
    status: 'assigned',
    requires_approval: false,
    visibility: 'private'
  });
  
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [isPersonalSelected, setIsPersonalSelected] = useState(getInitialPersonalState());
  const [descExpanded, setDescExpanded] = useState(false);

  const isEditing = !!editTask;

  // Fetch projects when workspace changes
  useEffect(() => {
    const fetchWorkspaceData = async () => {
      const wsIdToFetch = task.workspace_id;
      
      if (!wsIdToFetch) {
        setProjects([]);
        setMembers([]);
        return;
      }

      setLoadingProjects(true);
      try {
        const response = await api.get(`/workspaces/${wsIdToFetch}`);
        setProjects(response.data.projects || []);
        setMembers(response.data.members || []);
      } catch (error) {
        console.error('Failed to fetch workspace data:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchWorkspaceData();
  }, [task.workspace_id, workspaces]);

  // Reset project when workspace changes (only for new tasks)
  useEffect(() => {
    if (!defaultProjectId && !isEditing) {
      setTask(prev => ({ ...prev, project_id: '' }));
    }
  }, [task.workspace_id, defaultProjectId, isEditing]);

  // Set task data when modal opens or editTask changes
  useEffect(() => {
    if (open) {
      if (editTask) {
        // Editing existing task
        const editWsId = editTask.workspace_id || personalWsId || '';
        const editIsPersonal = workspaces.find(ws => ws.workspace_id === editWsId)?.workspace_type === 'personal';
        setIsPersonalSelected(!!editIsPersonal);
        setDescExpanded(false);
        setTask({
          title: editTask.title || '',
          description: editTask.description || '',
          workspace_id: editWsId,
          project_id: editTask.project_id || '',
          due_date: editTask.due_date ? new Date(editTask.due_date) : null,
          due_time: editTask.due_time || '',
          priority: editTask.priority || 'medium',
          assigned_to: editTask.assigned_to || '',
          is_recurring: editTask.is_recurring || false,
          recurrence_frequency: editTask.recurrence_rule?.frequency || 'weekly',
          recurrence_end_date: editTask.recurrence_rule?.end_date ? new Date(editTask.recurrence_rule.end_date) : null,
          value: editTask.value !== null && editTask.value !== undefined ? String(editTask.value) : '',
          status: editTask.status || 'assigned',
          requires_approval: editTask.requires_approval || false,
          visibility: editTask.visibility || 'private'
        });
      } else {
        // New task - default to personal workspace
        const wsId = defaultWorkspaceId || personalWsId || currentWorkspace?.workspace_id || '';
        const isPersonal = !defaultWorkspaceId || 
          workspaces.find(ws => ws.workspace_id === wsId)?.workspace_type === 'personal';
        setIsPersonalSelected(isPersonal);
        setTask({
          title: '',
          description: '',
          workspace_id: wsId,
          project_id: defaultProjectId || '',
          due_date: isPersonal ? new Date() : null,
          due_time: '',
          priority: 'medium',
          assigned_to: isPersonal ? (user?.user_id || '') : '',
          is_recurring: false,
          recurrence_frequency: 'weekly',
          recurrence_end_date: null,
          value: '',
          status: 'assigned',
          requires_approval: false
        });
      }
    }
  }, [open, editTask, defaultWorkspaceId, defaultProjectId, currentWorkspace]);

  const handleSubmit = async () => {
    if (!task.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    // Workspace is mandatory - but can be personal (empty)
    // Project is optional - user can choose "No Project"

    // Recurring tasks need a due date
    if (task.is_recurring && !task.due_date) {
      toast.error('Recurring tasks need a start date');
      return;
    }

    setLoading(true);
    try {
      // Build recurrence rule if recurring
      let recurrence_rule = null;
      if (task.is_recurring) {
        const freq = task.recurrence_frequency;
        recurrence_rule = {
          frequency: freq === 'biweekly' ? 'weekly' : freq,
          interval: freq === 'biweekly' ? 2 : 1
        };
        if (task.recurrence_end_date) {
          recurrence_rule.end_date = format(task.recurrence_end_date, 'yyyy-MM-dd');
        }
      }

      // Resolve workspace_id
      let resolvedWorkspaceId = task.workspace_id || personalWsId || null;

      const taskData = {
        title: task.title.trim(),
        description: task.description.trim(),
        workspace_id: resolvedWorkspaceId,
        project_id: task.project_id || null,
        due_date: task.due_date ? format(task.due_date, 'yyyy-MM-dd') : null,
        due_time: task.due_time || null,
        priority: task.priority,
        assigned_to: task.assigned_to || null,
        is_shared: !!resolvedWorkspaceId && !isPersonalSelected,
        is_recurring: task.is_recurring,
        recurrence_rule: recurrence_rule,
        value: task.value !== '' ? parseFloat(task.value) : null,
        status: task.status || 'assigned',
        requires_approval: task.requires_approval,
        visibility: isPersonalSelected ? 'private' : (task.visibility || 'private')
      };

      if (isEditing && editTask?.task_id) {
        // Update existing task
        await api.patch(`/tasks/${editTask.task_id}`, taskData);
        toast.success('Task updated successfully!');
      } else {
        // Create new task
        await api.post('/tasks', taskData);
        toast.success(task.is_recurring ? 'Recurring task created!' : 'Task created successfully!');
      }
      
      onTaskCreated?.();
      
      // Reset form
      const resetWsId = defaultWorkspaceId || personalWsId || currentWorkspace?.workspace_id || '';
      const resetIsPersonal = !defaultWorkspaceId || 
        workspaces.find(ws => ws.workspace_id === resetWsId)?.workspace_type === 'personal';
      setIsPersonalSelected(resetIsPersonal);
      setTask({
        title: '',
        description: '',
        workspace_id: resetWsId,
        project_id: defaultProjectId || '',
        due_date: resetIsPersonal ? new Date() : null,
        due_time: '',
        priority: 'medium',
        assigned_to: resetIsPersonal ? (user?.user_id || '') : '',
        is_recurring: false,
        recurrence_frequency: 'weekly',
        recurrence_end_date: null,
        value: '',
        status: 'assigned',
        requires_approval: false
      });
      
      onOpenChange?.(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} task`);
    } finally {
      setLoading(false);
    }
  };

  const getWorkspaceIcon = (type) => {
    switch (type) {
      case 'team': return <Users className="w-4 h-4" />;
      case 'client': return <Briefcase className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-lg dark:bg-slate-800 dark:border-slate-700 max-h-[90vh] flex flex-col p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4 border-b dark:border-slate-700 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 dark:text-gray-100 pr-8">
            <FolderKanban className="w-5 h-5 text-[#4A9CC7]" />
            {isEditing ? 'Edit Task' : 'Create Task'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 px-4 sm:px-6 overflow-y-auto flex-1 task-modal-scroll">
          {/* Task Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="dark:text-gray-300">Task Title *</Label>
            <Input
              id="title"
              value={task.title}
              onChange={(e) => setTask({ ...task, title: e.target.value })}
              placeholder="What needs to be done?"
              autoFocus
              data-testid="task-title-input"
              className="dark:bg-slate-900 dark:border-slate-700"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="dark:text-gray-300">Description</Label>
            {task.description && task.description.length > 200 && !descExpanded ? (
              <div>
                <div className="text-sm text-muted-foreground bg-muted/30 dark:bg-slate-900/40 rounded p-2 whitespace-pre-wrap break-words max-h-20 overflow-hidden" data-testid="task-desc-collapsed">
                  {task.description}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setDescExpanded(true)}
                    className="text-xs text-[#4A9CC7] hover:underline flex items-center gap-0.5"
                    data-testid="modal-expand-desc-btn"
                  >
                    <ChevronDown className="w-3 h-3" />Show Full &amp; Edit
                  </button>
                </div>
              </div>
            ) : (
              <Textarea
                id="description"
                value={task.description}
                onChange={(e) => setTask({ ...task, description: e.target.value })}
                placeholder="Add more details..."
                rows={task.description && task.description.length > 200 ? 8 : 2}
                className="dark:bg-slate-900 dark:border-slate-700"
                data-testid="task-description-input"
              />
            )}
          </div>

          {/* Workspace & Project Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label className="dark:text-gray-300">Workspace *</Label>
              <Select
                value={isPersonalSelected ? "personal" : (task.workspace_id || "personal")}
                onValueChange={(value) => {
                  if (value === "personal") {
                    setIsPersonalSelected(true);
                    setTask({ ...task, workspace_id: personalWsId, project_id: "", assigned_to: user?.user_id || '' });
                  } else {
                    setIsPersonalSelected(false);
                    setTask({ ...task, workspace_id: value, project_id: "", assigned_to: '' });
                  }
                }}
              >
                <SelectTrigger data-testid="workspace-select" className="dark:bg-slate-900 dark:border-slate-700">
                  <SelectValue placeholder="Select Workspace" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="personal">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      Personal
                    </div>
                  </SelectItem>
                  {workspaces.filter(ws => ws.workspace_type !== 'personal').map(ws => (
                    <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                      <div className="flex items-center gap-2">
                        {getWorkspaceIcon(ws.workspace_type)}
                        {ws.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="dark:text-gray-300">Project</Label>
              <Select
                value={task.project_id || "none"}
                onValueChange={(value) => setTask({ ...task, project_id: value === "none" ? "" : value })}
                disabled={(!task.workspace_id && !isPersonalSelected) || loadingProjects}
              >
                <SelectTrigger data-testid="project-select" className="dark:bg-slate-900 dark:border-slate-700">
                  <SelectValue placeholder={loadingProjects ? "Loading..." : "Select Project"} />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.length === 0 && !loadingProjects && (task.workspace_id || isPersonalSelected) && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground italic">No projects in this workspace</div>
                  )}
                  {projects.map(proj => (
                    <SelectItem key={proj.project_id} value={proj.project_id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm" 
                          style={{ backgroundColor: proj.color || '#4A9CC7' }}
                        />
                        {proj.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label className="dark:text-gray-300">Due Date</Label>
              {/* Native date input for mobile */}
              <div className="sm:hidden">
                <Input
                  type="date"
                  value={task.due_date ? format(task.due_date, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTask({ ...task, due_date: val ? new Date(val + 'T00:00:00') : null });
                  }}
                  className="dark:bg-slate-900 dark:border-slate-700"
                  data-testid="due-date-mobile"
                />
              </div>
              {/* Calendar popover for desktop */}
              <div className="hidden sm:block">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal dark:bg-slate-900 dark:border-slate-700"
                      data-testid="due-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {task.due_date ? format(task.due_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 dark:bg-slate-800 dark:border-slate-700" align="start">
                    <Calendar
                      mode="single"
                      selected={task.due_date}
                      onSelect={(date) => setTask({ ...task, due_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="dark:text-gray-300">Due Time</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="time"
                  value={task.due_time}
                  onChange={(e) => setTask({ ...task, due_time: e.target.value })}
                  className="pl-10 dark:bg-slate-900 dark:border-slate-700"
                  data-testid="due-time-input"
                />
              </div>
            </div>
          </div>

          {/* Priority & Assignee */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={task.priority}
                onValueChange={(value) => setTask({ ...task, priority: value })}
              >
                <SelectTrigger data-testid="priority-select" className="dark:bg-slate-900 dark:border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <Flag className={`w-4 h-4 ${p.color}`} />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select
                value={task.assigned_to || "unassigned"}
                onValueChange={(value) => setTask({ ...task, assigned_to: value === "unassigned" ? "" : value })}
                disabled={!task.workspace_id || members.length === 0}
              >
                <SelectTrigger data-testid="assignee-select" className="dark:bg-slate-900 dark:border-slate-700">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {members.map(member => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.name || member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Value Field */}
          <div className="space-y-2">
            <Label htmlFor="value">Value</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="value"
                type="number"
                step="any"
                value={task.value}
                onChange={(e) => setTask({ ...task, value: e.target.value })}
                placeholder="Enter a numeric value (optional)"
                className="pl-10 dark:bg-slate-900 dark:border-slate-700"
                data-testid="task-value-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">Optional numeric value for tracking or estimation</p>
          </div>

          {/* Recurring Task */}
          <div className="border dark:border-slate-700 rounded-lg p-4 bg-muted/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-[#4A9CC7]" />
                <Label className="text-sm font-medium dark:text-gray-300">Recurring Task</Label>
              </div>
              <Switch
                checked={task.is_recurring}
                onCheckedChange={(checked) => setTask({ ...task, is_recurring: checked })}
                data-testid="recurring-switch"
              />
            </div>
            {task.is_recurring && (
              <div className="mt-3 pt-3 border-t dark:border-slate-700 space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Repeat</Label>
                  <Select
                    value={task.recurrence_frequency}
                    onValueChange={(value) => setTask({ ...task, recurrence_frequency: value })}
                  >
                    <SelectTrigger className="mt-1 dark:bg-slate-900 dark:border-slate-700" data-testid="recurrence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      {RECURRENCE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End Date (optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 justify-start text-left font-normal dark:bg-slate-900 dark:border-slate-700 dark:text-gray-300"
                        data-testid="recurrence-end-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                        {task.recurrence_end_date ? format(task.recurrence_end_date, 'PPP') : 'No end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 dark:bg-slate-800 dark:border-slate-700" align="start">
                      <Calendar
                        mode="single"
                        selected={task.recurrence_end_date}
                        onSelect={(date) => setTask({ ...task, recurrence_end_date: date })}
                        initialFocus
                      />
                      {task.recurrence_end_date && (
                        <div className="p-2 border-t dark:border-slate-700">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs"
                            onClick={() => setTask({ ...task, recurrence_end_date: null })}
                            data-testid="clear-end-date-btn"
                          >
                            Clear end date
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-xs text-muted-foreground">
                  When completed, a new task will be created automatically{task.recurrence_end_date ? ` until ${format(task.recurrence_end_date, 'PP')}` : ''}
                </p>
              </div>
            )}
          </div>

          {/* Requires Approval */}
          <div className="border dark:border-slate-700 rounded-lg p-4 bg-muted/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-500" />
                <div>
                  <Label className="text-sm font-medium dark:text-gray-300">Requires Approval</Label>
                  <p className="text-xs text-muted-foreground">Task must be approved by manager before completion</p>
                </div>
              </div>
              <Switch
                checked={task.requires_approval}
                onCheckedChange={(checked) => setTask({ ...task, requires_approval: checked })}
                data-testid="requires-approval-switch"
              />
            </div>
          </div>

          {/* Visibility */}
          {!isPersonalSelected && (
            <div className="border dark:border-slate-700 rounded-lg p-4 bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#4A9CC7]" />
                  <div>
                    <Label className="text-sm font-medium dark:text-gray-300">Visible to Workspace</Label>
                    <p className="text-xs text-muted-foreground">All workspace members can see this task (including Private Contributors)</p>
                  </div>
                </div>
                <Switch
                  checked={task.visibility === 'workspace'}
                  onCheckedChange={(checked) => setTask({ ...task, visibility: checked ? 'workspace' : 'private' })}
                  data-testid="visibility-switch"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 sm:p-6 pt-2 sm:pt-4 border-t dark:border-slate-700 flex-shrink-0 gap-2">
          <DialogClose asChild>
            <Button variant="outline" className="dark:border-slate-600 dark:text-gray-300 w-full sm:w-auto" data-testid="cancel-task-btn">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleSubmit}
            disabled={loading || !task.title.trim()}
            className="bg-[#4A9CC7] hover:bg-[#3d8ab5] w-full sm:w-auto"
            data-testid="create-task-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Update Task' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedTaskModal;
